import React, { Fragment } from 'react';
import { Nav, NavItem, NavLink, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, } from 'reactstrap';
import { sortBy, keyBy, } from 'lodash';
import classnames from 'classnames';
import { toast } from 'react-toastify';

import firebase from '../firebase';
import TenantLink from './TenantLink';
import useTenant from './hooks/useTenant';
import useCollectionSubscriptionInTenant from './hooks/useCollectionSubscriptionInTenant';
import useSimulation from './hooks/useSimulation';
import ModalButton from './ModalButton';
import SimulationChangesTable from './SimulationChangesTable';

const db = firebase.firestore();
const simulationsRef = db.collection('simulations');

const tabs = [
  { name: 'schedule', label: '在庫スケジュール', path: '/schedule' },
  { name: 'suppliers', label: '仕入先', path: '/suppliers' },
  { name: 'purchaseOrders', label: 'PO', path: '/purchaseOrders' },
  { name: 'containers', label: 'コンテナ', path: '/containers' },
];
// マスタ共有のため、シミュレーションを引き継がないタブ
const masterOnlyTabNames = ['suppliers'];

export default function InventoriesHeader (props) {
  const { activeTab, } = props;
  const tenant = useTenant();
  const {
    simulationId, simulation, isSimulating, isSimulationMissing, isStale,
    isMasterLocked, isEditable, changes, removeChange,
  } = useSimulation();
  const simulations = sortBy(
    useCollectionSubscriptionInTenant(simulationsRef),
    _ => -(_.createdAt?.toDate?.().getTime() ?? 0)
  );
  const activeSimulations = simulations.filter(_ => _.appliedAt == null && _.staleAt == null);
  // 削除した行は一覧から消えるため、変更内容の見出しはマスタから拾う
  const purchaseOrders = useCollectionSubscriptionInTenant(isSimulating ? db.collection('purchaseOrders') : null, [isSimulating]);
  const containers = useCollectionSubscriptionInTenant(isSimulating ? db.collection('containers') : null, [isSimulating]);
  const mastersById = {
    purchaseOrders: keyBy(purchaseOrders, 'id'),
    containers: keyBy(containers, 'id'),
  };
  const onRemoveChange = async (collectionName, id) => {
    await removeChange(collectionName, id);
    toast.success('取り消しました');
  };

  const tabPath = ({ name, path }) => {
    const base = `/admin/inventories${path}`;
    return (isSimulating && !masterOnlyTabNames.includes(name))
      ? `${base}?simulationId=${simulationId}`
      : base;
  };
  // 切り替えても今見ているタブは維持する
  const currentTabPath = tabs.find(_ => _.name === activeTab)?.path ?? '/schedule';
  const switchTo = (nextSimulationId) => {
    const base = `/admin/inventories${currentTabPath}`;
    return nextSimulationId ? `${base}?simulationId=${nextSimulationId}` : base;
  };

  return (
    <div>
      <div className="d-flex justify-content-center mb-3">
        <h4>在庫管理</h4>
      </div>
      <div className="d-flex align-items-end border-bottom">
        <Nav tabs className="border-bottom-0" style={{ marginBottom: -1 }}>
          {
            tabs.map((tab) => {
              const { name, label } = tab;
              return (
                <NavItem key={name}>
                  <NavLink
                    tag={TenantLink}
                    className={classnames({ active: activeTab === name })}
                    to={tabPath(tab)}
                  >
                    {label}
                  </NavLink>
                </NavItem>
              );
            })
          }
        </Nav>
        <div className="pb-1 ml-3">
          <UncontrolledDropdown>
            <DropdownToggle caret color={isSimulating ? 'warning' : (isMasterLocked ? 'secondary' : 'light')} size="sm">
              {
                isSimulating
                  ? (simulation?.name ?? '読込中...')
                  : (isMasterLocked ? <Fragment><span className="fas fa-lock mr-1" />マスタ</Fragment> : 'マスタ')
              }
            </DropdownToggle>
            <DropdownMenu style={{ maxHeight: 400, overflowY: 'auto' }}>
              <DropdownItem tag={TenantLink} to={switchTo(null)} active={!isSimulating}>
                マスタ
              </DropdownItem>
              {
                activeSimulations.length > 0 && (
                  <Fragment>
                    <DropdownItem divider />
                    <DropdownItem header>シミュレーション</DropdownItem>
                    {
                      activeSimulations.map(_ => (
                        <DropdownItem key={_.id} tag={TenantLink} to={switchTo(_.id)} active={simulationId === _.id}>
                          {_.name}
                        </DropdownItem>
                      ))
                    }
                  </Fragment>
                )
              }
              <DropdownItem divider />
              <DropdownItem tag={TenantLink} to="/admin/inventories/simulations">
                <span className="fas fa-cog mr-1" />
                シミュレーション管理
              </DropdownItem>
            </DropdownMenu>
          </UncontrolledDropdown>
        </div>
      </div>
      {
        isSimulating ? (
          <div className={classnames('alert py-1 px-2 mb-0 mt-2', (isSimulationMissing || isStale) ? 'alert-danger' : 'alert-warning')}>
            <div className="small">
              <span className="fas fa-flask mr-1" />
              {
                isSimulationMissing ? (
                  'このシミュレーションは存在しません。削除された可能性があります。'
                ) : isStale ? (
                  <Fragment>
                    このシミュレーションは
                    {simulation?.appliedAt ? 'マスタに適用済み' : `失効しています（${simulation?.staleReason ?? '前提が変わったため'}）`}
                    です。閲覧のみ可能です。
                  </Fragment>
                ) : (
                  <Fragment>
                    シミュレーション「<strong>{simulation?.name}</strong>」を編集中です。
                    変更は
                    <ModalButton
                      color="link"
                      size="sm"
                      className="p-0 align-baseline"
                      label={`${changes.length}件`}
                      title={`変更内容 — ${simulation?.name}`}
                      modalProps={{ size: 'lg' }}
                      content={_ => (
                        <SimulationChangesTable
                          changes={changes}
                          mastersById={mastersById}
                          onRemove={isEditable ? onRemoveChange : undefined}
                        />
                      )}
                    />
                    。マスタには反映されません。
                  </Fragment>
                )
              }
            </div>
          </div>
        ) : isMasterLocked && (
          <div className="alert alert-secondary py-1 px-2 mb-0 mt-2">
            <div className="small">
              <span className="fas fa-lock mr-1" />
              シミュレーションの検討中のため、マスタの編集はできません。
              適用または破棄すると解除されます。
            </div>
          </div>
        )
      }
    </div>
  );
};
